import { Component, ChangeDetectionStrategy, signal, effect, Output, EventEmitter, input } from '@angular/core';
import { isValidUrlOrIpAddress } from '@local/ts-infra';
import { cloneDeep } from 'lodash';

export interface CustomizationInputsModel {
  content: CustomizationInput[];
  mode: 'editableRow' | 'constantRow' | 'newRow';
}

export type CustomizationInput = {
  id: 'text' | 'url';
  placeholder?: string;
  input?: string;
};

@Component({
  selector: 'slack-customization-inputs-list',
  templateUrl: './slack-customization-inputs-list.component.html',
  styleUrl: './slack-customization-inputs-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SlackCustomizationInputsListComponent {
  readonly BUTTON_TEXT_CHARS_LIMIT = 30;
  readonly ROWS_LIMIT = 3;
  readonly modelItem = signal<CustomizationInputsModel>({
    content: [
      { id: 'text', placeholder: 'Button name' },
      { id: 'url', placeholder: 'Paste the button URL' },
    ],
    mode: 'newRow',
  });
  model = signal<CustomizationInputsModel[]>([]);
  validModel = signal<CustomizationInputsModel[]>([]);
  errors = signal<string[]>(['', '']);
  limitCharMessages = signal<string[]>([]);
  focusInputs = signal<boolean>(true);

  initialModel = input<CustomizationInputsModel[]>([]);
  readonly = input<boolean>();

  @Output() modelChanged = new EventEmitter<CustomizationInputsModel[]>();

  constructor() {
    effect(
      () => {
        if (!this.initialModel()?.length) {
          this.model.set([cloneDeep(this.modelItem())]);
          this.focusInputs.set(false);
          return;
        }
        this.focusInputs.set(true);
        this.model.set(cloneDeep(this.initialModel()));
        if (this.initialModel().every((m) => this.checkIsRowValid(m))) {
          this.validModel.set(cloneDeep(this.initialModel()));
        }
      },
      { allowSignalWrites: true }
    );
  }

  onChangeItem($event, idxLine: number, idxColumn: number) {
    this.setLimitMessage($event, idxLine, idxColumn);
    this.errors.update((errors) => {
      const updatedErrors = [...errors];
      updatedErrors[idxColumn] = '';
      return updatedErrors;
    });
    this.model.update((modelArr) => {
      const updatedList = [...modelArr];
      updatedList[idxLine].content[idxColumn].input = $event;
      return updatedList;
    });
  }

  private setLimitMessage(value: string, idxLine: number, idxColumn: number) {
    if (idxColumn) {
      return;
    }
    let errorMessage: string = '';
    if (value?.length >= this.BUTTON_TEXT_CHARS_LIMIT) {
      errorMessage = 'Character limit reached';
    }
    this.limitCharMessages.update((errors) => {
      const updatedErrors = [...errors];
      updatedErrors[idxLine] = errorMessage;
      return updatedErrors;
    });
  }

  onFocusItem($event, idxLine: number, idxColumn: number) {
    this.setLimitMessage($event.value, idxLine, idxColumn);
  }

  onBlurItem($event, idxLine: number, idxColumn: number) {
    this.limitCharMessages.set([]);
    if (!$event.value) {
      this.errors.update((errors) => {
        const updatedErrors = [...errors];
        updatedErrors[idxColumn] = 'The field is empty';
        return updatedErrors;
      });
    } else if (idxColumn === 1) {
      if (!$event.value.startsWith('http://') && !$event.value.startsWith('https://')) {
        $event.value = `https://${$event.value}`;
        this.onChangeItem($event.value, idxLine, idxColumn);
      }
      if (!isValidUrlOrIpAddress($event.value)) {
        this.errors.update((errors) => {
          const updatedErrors = [...errors];
          updatedErrors[idxColumn] = 'The URL is invalid';
          return updatedErrors;
        });
      }
    }
  }

  checkIsRowValid(row): boolean {
    return row.content.every((input) => {
      const isInput = input.input && input.input.trim();
      const isValidUrl = input.id === 'url' ? isValidUrlOrIpAddress(input.input) : true;
      return isInput && isValidUrl;
    });
  }

  setInputsErrors(idxLine: number) {
    const element = this.model()[idxLine];
    element.content.forEach((i, index) => {
      this.onBlurItem({ value: i.input }, idxLine, index);
    });
  }

  onUpdatedRows($event) {
    this.model.set(cloneDeep($event.model));
    if (!$event.model?.length) {
      this.model.set([cloneDeep(this.modelItem())]);
      $event.model = [cloneDeep(this.modelItem())];
    }
    this.resetErrors();
    if ($event.action === 'add' || $event.action === 'edit') {
      return;
    }
    if ($event.model.every((m) => this.checkIsRowValid(m))) {
      this.validModel.set(cloneDeep($event.model.map((v) => ({ ...v, mode: 'constantRow' }))));
      this.modelChanged.emit(this.model());
    }
    if ($event.action === 'delete' && $event.model[0].mode === 'newRow') {
      this.modelChanged.emit(null);
    }
  }

  private resetErrors() {
    this.errors.set(['', '']);
  }
}
